import React, {useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {api} from '../../api';

const STAGES = [
  {key: 'DISCOVERY', label: 'Discovery', color: 'bg-purple-500'},
  {key: 'QUALIFIED', label: 'Qualified', color: 'bg-blue-500'},
  {key: 'PROPOSAL', label: 'Proposal', color: 'bg-yellow-500'},
  {key: 'NEGOTIATION', label: 'Negotiation', color: 'bg-orange-500'},
  {key: 'CLOSED_WON', label: 'Closed Won', color: 'bg-green-500'},
  {key: 'CLOSED_LOST', label: 'Closed Lost', color: 'bg-red-500'},
];

export default function DealsBoard({deals, onUpdate}) {
  const [dragDeal, setDragDeal] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const dragCounter = useRef({});

  const moveDeal = async (deal, newStage) => {
    if (deal.stage === newStage) return;
    await api(`/deals/${deal.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: deal.name, companyId: deal.companyId, value: deal.value, stage: newStage,
        contactIds: (deal.contacts || []).map(c => c.id), closeDate: deal.closeDate,
        tagIds: (deal.tags || []).map(t => t.id), notes: deal.notes || [],
      }),
    });
    onUpdate();
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" style={{minHeight: 400}}>
      {STAGES.map(stage => {
        const stageDeals = deals.filter(d => d.stage === stage.key);
        const total = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
        return (
          <div key={stage.key} className={`flex-shrink-0 w-64 rounded-lg bg-gray-50 flex flex-col ${overStage === stage.key ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
            onDragEnter={() => { dragCounter.current[stage.key] = (dragCounter.current[stage.key] || 0) + 1; setOverStage(stage.key); }}
            onDragLeave={() => { dragCounter.current[stage.key]--; if (dragCounter.current[stage.key] <= 0) { dragCounter.current[stage.key] = 0; setOverStage(null); } }}
            onDrop={e => { e.preventDefault(); dragCounter.current[stage.key] = 0; setOverStage(null); if (dragDeal) moveDeal(dragDeal, stage.key); }}>
            <div className="p-3 border-b">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                <span className="text-sm font-semibold text-gray-900">{stage.label}</span>
                <span className="text-xs text-gray-400 ml-auto">{stageDeals.length}</span>
              </div>
              {total > 0 && <p className="text-xs text-gray-500">${total.toLocaleString()}</p>}
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-y-auto" style={{maxHeight: 'calc(100vh - 280px)'}}>
              {stageDeals.map(deal => (
                <div key={deal.id} draggable
                  onDragStart={() => setDragDeal(deal)}
                  onDragEnd={() => { setDragDeal(null); setOverStage(null); }}
                  className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab active:cursor-grabbing hover:shadow ${dragDeal?.id === deal.id ? 'opacity-40' : ''}`}>
                  <Link to={`/crm/deals/${deal.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 block">{deal.name}</Link>
                  {deal.companyName && <p className="text-xs text-gray-500 mt-0.5">{deal.companyName}</p>}
                  <div className="flex items-center justify-between mt-2">
                    {deal.value != null ? <span className="text-sm font-semibold text-gray-900">${deal.value.toLocaleString()}</span> : <span />}
                    {deal.closeDate && <span className="text-xs text-gray-400">{deal.closeDate}</span>}
                  </div>
                  {deal.contacts?.length > 0 && (
                    <div className="flex -space-x-1 mt-2">
                      {deal.contacts.slice(0, 3).map(c => (
                        <div key={c.id} className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center border-2 border-white font-medium" title={`${c.firstName} ${c.lastName}`}>
                          {c.firstName?.[0]}{c.lastName?.[0]}
                        </div>
                      ))}
                      {deal.contacts.length > 3 && <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center border-2 border-white">+{deal.contacts.length - 3}</div>}
                    </div>
                  )}
                </div>
              ))}
              {stageDeals.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No deals</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
